import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Select, FormControl, InputLabel,
  Box, CircularProgress, Alert, Autocomplete
} from '@mui/material';
import { nodeNetworkService } from '../services/nodeNetworkService';

const CONNECTION_TYPES = ['supply', 'transfer', 'oversight', 'verification', 'data_exchange'];

/**
 * NodeConnectionManager
 *
 * Reusable dialog for creating a new connection FROM the current node
 * TO another node in the same network.
 *
 * Props:
 *   open        {boolean}
 *   onClose     {function}
 *   nodeId      {string}   - the source node UUID
 *   networkId   {string}   - used to load candidate target nodes
 *   onCreated   {function} - called after successful creation
 */
function NodeConnectionManager({ open, onClose, nodeId, networkId, onCreated }) {
  const [nodes, setNodes] = useState([]);
  const [form, setForm] = useState({
    to_node_id: null,
    connection_type: 'supply',
    pact_connection_id: '',
    client_id: '',
    expires_at: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && networkId) {
      loadNodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, networkId]);

  const loadNodes = async () => {
    try {
      const data = await nodeNetworkService.getNodes(networkId);
      // Exclude self
      setNodes((data ?? []).filter(n => n.node_id !== nodeId));
    } catch (err) {
      setError('Failed to load nodes: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSubmit = async () => {
    if (!form.to_node_id) return;
    try {
      setLoading(true);
      setError(null);
      await nodeNetworkService.createConnection(nodeId, {
        to_node_id: form.to_node_id.node_id,
        connection_type: form.connection_type,
        pact_connection_id: form.pact_connection_id || undefined,
        client_id: form.client_id || undefined,
        expires_at: form.expires_at || undefined
      });
      setForm({ to_node_id: null, connection_type: 'supply', pact_connection_id: '', client_id: '', expires_at: '' });
      onCreated && onCreated();
      onClose();
    } catch (err) {
      setError('Failed to create connection: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setForm({ to_node_id: null, connection_type: 'supply', pact_connection_id: '', client_id: '', expires_at: '' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Connection</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Autocomplete
            options={nodes}
            getOptionLabel={n => `${n.node_name} (${n.node_type})`}
            value={form.to_node_id}
            onChange={(_, value) => setForm(f => ({ ...f, to_node_id: value }))}
            renderInput={params => <TextField {...params} label="Target Node" required />}
          />
          <FormControl fullWidth>
            <InputLabel>Connection Type</InputLabel>
            <Select
              value={form.connection_type}
              label="Connection Type"
              onChange={e => setForm(f => ({ ...f, connection_type: e.target.value }))}
            >
              {CONNECTION_TYPES.map(t => (
                <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="PACT Connection ID (optional)"
            value={form.pact_connection_id}
            onChange={e => setForm(f => ({ ...f, pact_connection_id: e.target.value }))}
            fullWidth
            helperText="Reference to a PACT Directory Service connection"
          />
          <TextField
            label="OAuth2 Client ID (optional)"
            value={form.client_id}
            onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
            fullWidth
            helperText="For PACT peer-to-peer data exchange"
          />
          <TextField
            label="Expires At (optional)"
            type="date"
            value={form.expires_at}
            onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!form.to_node_id || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Create Connection
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NodeConnectionManager;
